import React, { useEffect, useState, useRef } from "react";
import { Search, Store, Package, TrendingUp, Loader2 } from "lucide-react";
import { productService, sellerService } from "@b2b/services";
import { PLACEHOLDER_PRODUCT_IMAGE } from "./placeholderProductImage";
import { getProductDetailShareUrl } from "../../lib/productRoutes";

interface SearchSuggestionsProps {
  searchQuery: string;
  isOpen: boolean;
  onClose: () => void;
  onSelectProduct?: (productId: string) => void;
  onSelectShop?: (shopId: string) => void;
  onSelectSuggestion?: (suggestion: string) => void;
  onSearchQueryChange?: (query: string) => void; // New prop to update search query
}

interface Product {
  id: string;
  name: string;
  description?: string;
  sku?: string;
  price?: number;
  currency?: string;
  thumbnailImage?: string;
  images?: string[];
}

interface Shop {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  company?: {
    name: string;
  };
  logo?: string;
}

// Initial popular search suggestions - shown when there's no query
const INITIAL_POPULAR_SUGGESTIONS = [
  "electronics",
  "machinery",
  "textiles",
  "packaging",
  "construction materials",
  "automotive parts",
  "medical devices",
  "furniture",
];

export const SearchSuggestions: React.FC<SearchSuggestionsProps> = ({
  searchQuery,
  isOpen,
  onClose,
  onSelectProduct,
  onSelectShop,
  onSelectSuggestion,
  onSearchQueryChange,
}) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [shops, setShops] = useState<Shop[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [popularSuggestions, setPopularSuggestions] = useState<string[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Refs to track current query and prevent stale updates
  const currentQueryRef = useRef<string>("");
  const suggestionsQueryRef = useRef<string>("");
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const suggestionsTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef<boolean>(true);

  // Minimum query length to trigger API calls (reduces unnecessary requests)
  const MIN_QUERY_LENGTH = 2;
  const DEBOUNCE_DELAY = 500; // Increased from 200-300ms to 500ms for better performance
  const SUGGESTIONS_DEBOUNCE_DELAY = 400; // Slightly faster for suggestions

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (suggestionsTimerRef.current) {
        clearTimeout(suggestionsTimerRef.current);
      }
    };
  }, []);

  // Fetch popular suggestions based on query
  useEffect(() => {
    if (!isOpen) {
      setPopularSuggestions([]);
      suggestionsQueryRef.current = "";
      return;
    }

    // Clear previous timer
    if (suggestionsTimerRef.current) {
      clearTimeout(suggestionsTimerRef.current);
    }

    const query = searchQuery?.trim() || "";
    suggestionsQueryRef.current = query;

    // Only fetch if query is empty (show initial suggestions) or query length >= MIN_QUERY_LENGTH
    if (query.length > 0 && query.length < MIN_QUERY_LENGTH) {
      setPopularSuggestions([]);
      return;
    }

    suggestionsTimerRef.current = setTimeout(() => {
      // Only fetch if query hasn't changed
      if (suggestionsQueryRef.current === query && isMountedRef.current) {
        fetchPopularSuggestions(query);
      }
    }, SUGGESTIONS_DEBOUNCE_DELAY);

    return () => {
      if (suggestionsTimerRef.current) {
        clearTimeout(suggestionsTimerRef.current);
      }
    };
  }, [searchQuery, isOpen]);

  // Debounce search API calls
  useEffect(() => {
    if (!isOpen) {
      setProducts([]);
      setShops([]);
      currentQueryRef.current = "";
      return;
    }

    const query = searchQuery.trim();

    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    // Track previous query to detect changes
    const previousQuery = currentQueryRef.current;

    // If query is empty, clear results immediately
    if (!query) {
      setProducts([]);
      setShops([]);
      currentQueryRef.current = "";
      // Reset loading state when clearing
      setLoading(false);
      setError(null);
      return;
    }

    // Only make API call if query length >= MIN_QUERY_LENGTH
    if (query.length < MIN_QUERY_LENGTH) {
      setProducts([]);
      setShops([]);
      currentQueryRef.current = "";
      setLoading(false);
      return;
    }

    // If query changed (including from empty to non-empty), clear previous results
    // This ensures fresh results when user clears and re-enters the same query
    if (previousQuery !== query) {
      setProducts([]);
      setShops([]);
      setError(null);
      // Reset loading to show fresh loading state for new query
      setLoading(true);
    }

    // Update ref immediately to track the current query
    currentQueryRef.current = query;

    debounceTimerRef.current = setTimeout(() => {
      // Only fetch if query hasn't changed and component is still mounted
      if (currentQueryRef.current === query && isMountedRef.current) {
        fetchSearchResults(query);
      }
    }, DEBOUNCE_DELAY);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [searchQuery, isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Helper function to check if a string matches the search query
  // Uses word boundary matching for better accuracy, but also allows partial matches
  const matchesSearch = (
    text: string | null | undefined,
    searchQuery: string
  ): boolean => {
    if (!text || !searchQuery) return false;
    const normalizedText = text.toLowerCase().trim();
    const normalizedQuery = searchQuery.toLowerCase().trim();

    // Exact match
    if (normalizedText === normalizedQuery) return true;

    // Starts with match (for better UX - handles partial queries like "pean" matching "peanut")
    if (normalizedText.startsWith(normalizedQuery)) return true;

    // Word boundary match - check if query appears as a complete word
    const wordBoundaryRegex = new RegExp(
      `\\b${normalizedQuery.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`,
      "i"
    );
    if (wordBoundaryRegex.test(normalizedText)) return true;

    // For queries >= 3 chars, allow substring match (handles partial word matches)
    // This ensures "pean" matches "peanut" even though it's not a word boundary
    if (normalizedQuery.length >= 3 && normalizedText.includes(normalizedQuery))
      return true;

    // For very short queries (1-2 chars), allow substring match
    if (normalizedQuery.length <= 2 && normalizedText.includes(normalizedQuery))
      return true;

    return false;
  };

  // Filter products to only show those that match by product name only
  const filterProducts = (
    products: Product[],
    searchQuery: string
  ): Product[] => {
    if (!searchQuery.trim()) return products;

    return products.filter((product) => {
      // Only check product name
      return matchesSearch(product.name, searchQuery);
    });
  };

  // Filter shops to only show those that match by seller name or company name only
  const filterShops = (shops: Shop[], searchQuery: string): Shop[] => {
    if (!searchQuery.trim()) return shops;

    return shops.filter((shop) => {
      // Check seller name (first name + last name)
      const sellerFullName =
        `${shop.firstName || ""} ${shop.lastName || ""}`.trim();
      const sellerNameMatch = matchesSearch(sellerFullName, searchQuery);

      // Check company name
      const companyNameMatch = shop.company?.name
        ? matchesSearch(shop.company.name, searchQuery)
        : false;

      // Only match if seller name OR company name matches
      return sellerNameMatch || companyNameMatch;
    });
  };

  const fetchPopularSuggestions = async (query: string) => {
    // Check if query has changed or component is unmounted
    if (!isMountedRef.current || suggestionsQueryRef.current !== query) {
      return;
    }

    setLoadingSuggestions(true);
    try {
      // Pass undefined if query is empty, otherwise pass the query
      const queryParam = query.trim().length > 0 ? query.trim() : undefined;
      const response = await productService.getPopularSuggestions(
        queryParam,
        8 // Limit to 8 suggestions
      );

      // Check again after async operation (query might have changed)
      if (!isMountedRef.current || suggestionsQueryRef.current !== query) {
        return;
      }

      // Handle both nested and flat response structures
      // API returns: { success: true, data: { suggestions: [...], message: "...", success: true } }
      // Service should unwrap it, but handle both cases
      const responseAny = response as any;
      const rawSuggestions =
        responseAny?.suggestions || responseAny?.data?.suggestions || [];

      // Filter out hashtags from suggestions
      const suggestions = rawSuggestions
        .map((s: string) => {
          if (!s) return "";
          // Remove all # characters and trim
          return s.replace(/#/g, "").trim();
        })
        .filter((s: string) => s.length > 0 && !s.startsWith("#")); // Remove empty strings and any that still start with #

      // Debug logging (can be removed in production)
      if (queryParam) {
        console.log("Popular suggestions response:", {
          response,
          suggestions,
          count: suggestions.length,
        });
      }

      // Final check before updating state
      if (!isMountedRef.current || suggestionsQueryRef.current !== query) {
        return;
      }

      // Always set the suggestions from API if available
      if (suggestions.length > 0) {
        setPopularSuggestions(suggestions);
      } else if (queryParam) {
        // If we have a query but no suggestions, set empty (will show fallback in UI)
        setPopularSuggestions([]);
      } else {
        // No query, keep initial suggestions
        setPopularSuggestions(INITIAL_POPULAR_SUGGESTIONS);
      }
    } catch (err: any) {
      // Ignore errors if query has changed or component unmounted
      if (!isMountedRef.current || suggestionsQueryRef.current !== query) {
        return;
      }

      // Only log non-abort errors
      if (err?.name !== "AbortError" && err?.code !== "ERR_CANCELED") {
        console.error("Error fetching popular suggestions:", err);
      }

      // On error, set empty array if query exists, otherwise keep initial suggestions
      const queryParam = query.trim().length > 0 ? query.trim() : undefined;
      setPopularSuggestions(queryParam ? [] : INITIAL_POPULAR_SUGGESTIONS);
    } finally {
      // Only update loading state if query hasn't changed
      if (isMountedRef.current && suggestionsQueryRef.current === query) {
        setLoadingSuggestions(false);
      }
    }
  };

  const fetchSearchResults = async (query: string) => {
    // Check if query has changed or component is unmounted
    if (!isMountedRef.current || currentQueryRef.current !== query) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch products and shops in parallel
      // Use getProducts with search query parameter for compatibility
      // Use getSellers instead of searchSellers since /sellers/search endpoint doesn't exist
      // Increased limit to account for filtering
      // Backend API expects 'search' parameter, but ProductSearchParams interface uses 'query'
      // The service passes params as-is, so we need to match backend expectations
      const productParams: any = {
        page: 1,
        limit: 50, // Fetch more products to account for client-side filtering of partial matches
      };
      // Add search parameter - backend expects 'search' not 'query'
      // For partial queries, we still send to backend but rely more on client-side filtering
      if (query) {
        productParams.search = query;
      }

      const [productsResponse, shopsResponse] = await Promise.all([
        productService.getProducts(productParams),
        sellerService.getSellers({
          search: query,
          page: 1,
          limit: 12, // Fetch more to account for client-side filtering
        }),
      ]);

      // Check again after async operation (query might have changed)
      if (!isMountedRef.current || currentQueryRef.current !== query) {
        return;
      }

      // Handle both response structures for products
      // ProductsResponse has products directly, but backend might wrap it in data
      const allProducts =
        (productsResponse as any).data?.products ||
        productsResponse.products ||
        [];
      const allShops = shopsResponse.sellers || [];

      // Client-side filtering to ensure only matching results are shown
      const filteredProducts = filterProducts(allProducts, query).slice(0, 6);
      const filteredShops = filterShops(allShops, query).slice(0, 6);

      // Final check before updating state
      if (!isMountedRef.current || currentQueryRef.current !== query) {
        return;
      }

      setProducts(filteredProducts);
      setShops(filteredShops);
    } catch (err: any) {
      // Ignore errors if query has changed or component unmounted
      if (!isMountedRef.current || currentQueryRef.current !== query) {
        return;
      }

      // Only log non-abort errors
      if (err?.name !== "AbortError" && err?.code !== "ERR_CANCELED") {
        console.error("Error fetching search results:", err);
        setError("Failed to load search results");
      }

      setProducts([]);
      setShops([]);
    } finally {
      // Only update loading state if query hasn't changed
      if (isMountedRef.current && currentQueryRef.current === query) {
        setLoading(false);
      }
    }
  };

  const handleProductClick = (productId: string) => {
    if (onSelectProduct) {
      onSelectProduct(productId);
    } else {
      // Default behavior: navigate to product page
      const url = getProductDetailShareUrl(productId);
      window.open(url, "_blank", "noopener,noreferrer");
    }
    onClose();
  };

  const handleShopClick = (shopId: string) => {
    if (onSelectShop) {
      onSelectShop(shopId);
    } else {
      // Default behavior: navigate to shop page
      const url = `${window.location.origin}/seller_detail/${shopId}`;
      window.open(url, "_blank", "noopener,noreferrer");
    }
    onClose();
  };

  const handleSuggestionClick = (suggestion: string) => {
    // Navigate to search page with the selected suggestion
    if (suggestion.trim()) {
      const url = `${window.location.origin}/search/${encodeURIComponent(suggestion.trim())}`;
      window.open(url, "_blank", "noopener,noreferrer");
    }

    // Also call the onSelectSuggestion callback if provided
    if (onSelectSuggestion) {
      onSelectSuggestion(suggestion);
    }

    // Close the dropdown after navigation
    onClose();
  };

  const handleViewAllClick = () => {
    if (searchQuery.trim()) {
      const url = `${window.location.origin}/search/${encodeURIComponent(searchQuery.trim())}`;
      window.open(url, "_blank", "noopener,noreferrer");
    }
    onClose();
  };

  if (!isOpen) return null;

  const hasResults = products.length > 0 || shops.length > 0;
  const hasQuery = searchQuery.trim().length > 0;

  // Always show suggestions section (same behavior as Products/Shops sections)
  // They're always visible when dropdown is open
  const showPopularSuggestions = true;

  // Determine which suggestions to show:
  // - If no query: show initial popular suggestions
  // - If query exists: show API suggestions (only show initial if API suggestions are empty AND not loading)
  const suggestionsToShow = hasQuery
    ? popularSuggestions.length > 0
      ? popularSuggestions
      : loadingSuggestions
        ? []
        : INITIAL_POPULAR_SUGGESTIONS // Only fallback if not loading
    : INITIAL_POPULAR_SUGGESTIONS;

  return (
    <div
      ref={containerRef}
      className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-[600px] overflow-y-auto"
    >
      {/* Popular Suggestions Section - Always show, similar to Products/Shops sections */}
      {showPopularSuggestions && (
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="h-4 w-4 text-[#F97316]" />
            <h3 className="text-sm font-semibold text-gray-700">
              {hasQuery ? "Related Suggestions" : "Popular Suggestions"}
            </h3>
            {loadingSuggestions && (
              <Loader2 className="h-3 w-3 animate-spin text-[#F97316]" />
            )}
          </div>
          {loadingSuggestions ? (
            <div className="flex items-center gap-2 py-2">
              <Loader2 className="h-4 w-4 animate-spin text-[#F97316]" />
              <span className="text-xs text-gray-500">
                Loading suggestions...
              </span>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {suggestionsToShow.map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-[#F97316] hover:text-white rounded-full transition-colors text-gray-700"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Products Section - Show even while loading */}
      {(loading || products.length > 0) && (
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Package className="h-4 w-4 text-[#F97316]" />
              <h3 className="text-sm font-semibold text-gray-700">Products</h3>
              {loading && (
                <Loader2 className="h-3 w-3 animate-spin text-[#F97316]" />
              )}
            </div>
            {products.length >= 6 && !loading && (
              <button
                onClick={handleViewAllClick}
                className="text-xs text-[#F97316] hover:underline"
              >
                View all
              </button>
            )}
          </div>
          {loading && products.length === 0 ? (
            <div className="flex items-center gap-2 py-4">
              <Loader2 className="h-4 w-4 animate-spin text-[#F97316]" />
              <span className="text-xs text-gray-500">Loading products...</span>
            </div>
          ) : error && products.length === 0 ? (
            <div className="text-xs text-red-600 py-2">{error}</div>
          ) : products.length > 0 ? (
            <div className="space-y-2">
              {products.map((product) => {
                const imageUrl =
                  product.thumbnailImage ||
                  (product.images && product.images.length > 0
                    ? product.images[0]
                    : null);
                const price = product.price
                  ? `${product.currency || "USD"} ${product.price.toFixed(2)}`
                  : "Price on request";

                return (
                  <button
                    key={product.id}
                    onClick={() => handleProductClick(product.id)}
                    className="w-full flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg transition-colors text-left"
                  >
                    <img
                      src={imageUrl || PLACEHOLDER_PRODUCT_IMAGE}
                      alt={product.name}
                      className="w-12 h-12 object-cover rounded border border-gray-200"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src =
                          PLACEHOLDER_PRODUCT_IMAGE;
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {product.name}
                      </p>
                      <p className="text-xs text-gray-500">{price}</p>
                    </div>
                    <Search className="h-4 w-4 text-gray-400" />
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
      )}

      {/* Shops Section - Show even while loading */}
      {(loading || shops.length > 0) && (
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Store className="h-4 w-4 text-[#F97316]" />
              <h3 className="text-sm font-semibold text-gray-700">Shops</h3>
              {loading && (
                <Loader2 className="h-3 w-3 animate-spin text-[#F97316]" />
              )}
            </div>
            {shops.length >= 6 && !loading && (
              <button
                onClick={handleViewAllClick}
                className="text-xs text-[#F97316] hover:underline"
              >
                View all
              </button>
            )}
          </div>
          {loading && shops.length === 0 ? (
            <div className="flex items-center gap-2 py-4">
              <Loader2 className="h-4 w-4 animate-spin text-[#F97316]" />
              <span className="text-xs text-gray-500">Loading shops...</span>
            </div>
          ) : error && shops.length === 0 ? (
            <div className="text-xs text-red-600 py-2">{error}</div>
          ) : shops.length > 0 ? (
            <div className="space-y-2">
              {shops.map((shop) => {
                const shopName =
                  shop.company?.name ||
                  `${shop.firstName || ""} ${shop.lastName || ""}`.trim() ||
                  "Shop";
                const logoUrl = shop.logo;

                return (
                  <button
                    key={shop.id}
                    onClick={() => handleShopClick(shop.id)}
                    className="w-full flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg transition-colors text-left"
                  >
                    {logoUrl ? (
                      <img
                        src={logoUrl}
                        alt={shopName}
                        className="w-12 h-12 object-cover rounded border border-gray-200"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                    ) : (
                      <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                        <Store className="h-5 w-5 text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {shopName}
                      </p>
                      <p className="text-xs text-gray-500">Verified Seller</p>
                    </div>
                    <Search className="h-4 w-4 text-gray-400" />
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
      )}

      {/* No Results - Only show when all loading is complete and no results */}
      {!loading &&
        !loadingSuggestions &&
        !error &&
        searchQuery.trim() &&
        products.length === 0 &&
        shops.length === 0 &&
        popularSuggestions.length === 0 && (
          <div className="p-8 text-center">
            <p className="text-sm text-gray-600">
              No results found for "{searchQuery}"
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Try different keywords or check spelling
            </p>
          </div>
        )}
    </div>
  );
};
